'use client';
import { isEmpty } from "lodash";
import { useParams } from "next/navigation";
import { NotFound } from "../NotFound";
import Link from "next/link";
import Image from "next/image";
import { libraryData } from "@/constant/resource";

type ResourceItem = {
    href: string;
    target?: string;
    img: { src: string; alt: string };
    tag?: string;
    date?: {
        day: string;
        date: string;
        year: string;
        time: string;
        tz: string;
    };
    title: string;
    desc?: string;
    linkText: string;
};

type ResourceSection = {
    id: string;
    title?: string;
    description?: string;
    items?: readonly unknown[];
    [key: string]: unknown;
};

export function ResourceDetails() {
    const params = useParams();
    const slug = params?.slug || '' as string;

    const currentResource = libraryData.sections.find(
        (section) => section.id === slug
    ) as ResourceSection | undefined;

    if (!currentResource || isEmpty(currentResource)) {
        return (
            <NotFound />
        );
    }

    return (
        <>
            <section className="section-blog-header">
                <div className="padding-global">
                    <div className="container-large">
                        <div className="blog-header_content-wrapper">
                            <h1 id="page-title" className="heading-large">{currentResource.title}</h1>
                            {
                                currentResource?.description &&
                                <div className="margin-top margin-xsmall">
                                    <div className="text-size-medium" dangerouslySetInnerHTML={{ __html: currentResource.description }} />
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </section>
            <section className="section-library-all margin-bottom margin-medium">
                <div className="padding-global">
                    <div className="container-large">
                        <PostsList section={currentResource} isFull={true}/>
                    </div>
                </div>
            </section>
        </>
    )
}

// PostsItem component
export function PostsItem({ item, sectionId }: { item: ResourceItem; sectionId: string }) {
    return (
        <div key={item.href} role="listitem" className="posts_item w-dyn-item">
            <Link href={item.href} target={item.target} className="posts_item-link w-inline-block">
                <div className="posts_content-wrapper">
                    {/* Explicit fixed height so the thumbnail reserves its space
                        before the image loads. The Webflow `.posts_image-wrapper`
                        height was being overridden by the image's intrinsic size on
                        this route, so the card grew on load and shifted the content
                        below it (Lighthouse CLS). object-fit:cover crops to fit. */}
                    <div
                        className={`posts_image-wrapper${sectionId === "events" ? " is-large-thumb" : ""}`}
                        style={{ height: sectionId === "events" ? "16rem" : "12.5rem" }}
                    >

                        <Image
                            src={item.img.src}
                            unoptimized
                            width={400}
                            height={200}
                            alt={item.img.alt}
                            sizes="100vw"
                            className={`posts_image${sectionId === "events" ? " is-top-left" : ""}`}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    </div>
                    <div className="posts_content">
                        <div className="posts_content-top">
                            {item?.tag && (
                                <div className="tag mb-3">
                                    <div className="tag_label is-featured">{item.tag}</div>
                                </div>
                            )}
                            {item.date && (
                                <div className="posts_date-wrapper">
                                    <div className="posts_day">
                                        <div className="text-size-tiny">{item.date.day}</div>
                                        <div className="text-size-tiny">,</div>
                                    </div>
                                    <div className="posts_date">
                                        <div className="text-size-tiny">{item.date.date}</div>
                                        <div className="text-size-tiny">{item.date.year}</div>
                                        {item.date.time && (
                                            <>
                                                <div className="text-size-tiny">at</div>
                                                <div className="text-size-tiny">{item.date.time}</div>
                                                <div className="text-size-tiny">{item.date.tz}</div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                            {/* h2 (not h3) so the heading order is h1 -> h2 with no
                                skipped level (Lighthouse heading-order); the visual
                                size comes from .text-size-regular, so it is unchanged. */}
                            <h2 className="text-size-regular">{item.title}</h2>
                            {item.desc && (
                                <div className="margin-top margin-xxsmall">
                                    <div className="text-size-small text-style-2lines">
                                        <p dangerouslySetInnerHTML={{ __html: item.desc }}></p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="posts_content-bottom">
                            <div className="link">
                                <div className="link_content-wrapper">
                                    <div className="link_text text-size-small">{item.linkText}</div>
                                    {(sectionId === "events" || sectionId === "webinars" || sectionId === "guides") && (
                                        <div className="link_icon w-embed">
                                            <ArrowIcon />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
        </div>
    );
}

// PostsList component
export function PostsList({ section, isFull }: { section: ResourceSection, isFull: boolean }) {
    const allItems = (section.items ?? []) as unknown as ResourceItem[];
    const visibleItems = isFull ? allItems : allItems.slice(0, 4);

    return (
        <div className="posts_list-wrapper w-dyn-list">
            {visibleItems.length ? (
                // role="list" must contain only role="listitem" children (axe
                // aria-required-children), so the empty-state message is rendered
                // as a sibling below, never as a child of this container.
                <div
                    role="list"
                    className={`posts_list${section.id === "events" ? " is-large" : ""} w-dyn-items`}
                >
                    {visibleItems.map((item) => (
                        <PostsItem key={item.href} item={item} sectionId={section.id} />
                    ))}
                </div>
            ) : (
                <p className="posts_list-empty">No {section.id} yet. Check back soon.</p>
            )}
        </div>
    );
}


export function ArrowIcon() {
    return (
        <svg aria-hidden="true" role="img" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
            <g fill="currentColor">
                <path
                    d="M1.293,10.707a1,1,0,0,0,1.414,0L7.854,5.561l2.292,2.293A.5.5,0,0,0,10.5,8a.489.489,0,0,0,.191-.038A.5.5,0,0,0,11,7.5v-6a.5.5,0,0,0-.5-.5h-6a.5.5,0,0,0-.354.854L6.439,4.146,1.293,9.293A1,1,0,0,0,1.293,10.707Z"
                    fill="currentColor"
                ></path>
            </g>
        </svg>
    );
}